/**
 * DEFAULT {@link LocationTracker}: continuous GPS capture over the Sessions
 * position stream in `services/background-location.ts`.
 *
 * The stream decides where fixes come from:
 *  - in the native shells, while a session journey / checked-in session is
 *    active, the native BackgroundLocation plugin (Android location foreground
 *    service / iOS Core Location background updates) keeps capturing with the
 *    screen locked or Maps in front and buffers fixes until JS drains them;
 *  - otherwise (web, plugin unavailable, permission refused) it is the plain
 *    `navigator.geolocation.watchPosition` — the foreground-only behaviour the
 *    tracker always had, with NO native config required.
 *
 * REMAINING LIMITATIONS:
 * - No native motion classification (`in_vehicle`/`on_foot`); driving vs
 *   walking is inferred from the speed heuristic in `shared/sessions/km.ts`,
 *   optionally corrected by the worker's manual "I'm driving / not driving"
 *   override.
 * - `isMock` is only populated by the native plugin (platform mock flag); the
 *   browser cannot report it.
 * - A force-stopped / OS-killed app stops capturing; samples persisted so far
 *   are kept by the active-session store and natively buffered fixes are
 *   drained on the next launch.
 *
 * The capture concern lives here. Optional cloud "accuracy refinement"
 * (snap-to-roads, geocoding) is deliberately OUT OF SCOPE for the tracker — see
 * `docs/sessions-location-tracking.md`.
 */

import type { GeoStamp } from '@shared/sessions/types'
import {
  isPositionWatchSupported,
  watchSessionPosition,
} from '../../../services/background-location'
import {
  GeolocationError,
  type LocationProvider,
  type LocationTracker,
  BrowserLocationProvider,
} from './location-provider'

export class WebLocationTracker implements LocationTracker {
  private unsubscribe: (() => void) | null = null
  private samples: GeoStamp[] = []
  private onSample: ((sample: GeoStamp) => void) | null = null

  constructor(private readonly provider: LocationProvider = new BrowserLocationProvider()) {}

  /** One-shot fix — delegated to the browser provider. */
  getCurrentPosition(): Promise<GeoStamp> {
    return this.provider.getCurrentPosition()
  }

  startTracking(onSample: (sample: GeoStamp) => void): Promise<void> {
    if (!isPositionWatchSupported()) {
      return Promise.reject(
        new GeolocationError('Geolocation is not supported on this device.', 'UNSUPPORTED'),
      )
    }

    // Already tracking: keep the existing subscription but adopt the new callback.
    if (this.unsubscribe) {
      this.onSample = onSample
      return Promise.resolve()
    }

    // Fresh capture session. Resolve eagerly — a first fix can take a while and
    // the session must not block on GPS; samples accumulate as they arrive.
    // Once tracking has begun, transient watcher errors are swallowed: a
    // dropped fix must never abort a live session.
    this.samples = []
    this.onSample = onSample
    this.unsubscribe = watchSessionPosition((sample) => {
      this.samples.push(sample)
      this.onSample?.(sample)
    })
    return Promise.resolve()
  }

  stopTracking(): Promise<GeoStamp[]> {
    this.unsubscribe?.()
    this.unsubscribe = null
    const track = this.samples
    this.samples = []
    this.onSample = null
    return Promise.resolve(track)
  }
}

let instance: LocationTracker | null = null

/**
 * Process-wide {@link LocationTracker} singleton (lazily created).
 *
 * SEAM: this is the ONLY place that decides which capture engine is active.
 * The engine itself is platform-adaptive through the position stream (see
 * `services/background-location.ts`), so there is a single tracker class.
 */
export function getLocationTracker(): LocationTracker {
  if (!instance) {
    instance = new WebLocationTracker()
  }
  return instance
}

/** Test/demo hook: override the active location tracker. */
export function setLocationTracker(tracker: LocationTracker): void {
  instance = tracker
}
